import { useState } from 'react'
import {
  addLink,
  hostOf,
  linkTitle,
  removeLink,
  safeHref,
  type LinkRow,
} from '../lib/linksApi'

interface Props {
  token: string
  links: LinkRow[]
  /**
   * Whether this person may add or remove here. Convenience only — the
   * database re-checks with `can_link`, so a wrong value here cannot grant
   * anything, it only shows or hides a button.
   */
  canEdit: boolean
  /** Exactly one of these. */
  taskId?: string
  issueId?: string
  onChanged: () => void
}

/**
 * The link list shown on a task card and an issue card.
 *
 * Shared rather than duplicated per card, because the "who can edit" rule and
 * the URL rendering are the two things that must not drift between the two
 * places they appear.
 */
export default function LinkList({
  token,
  links,
  canEdit,
  taskId,
  issueId,
  onChanged,
}: Props) {
  const [adding, setAdding] = useState(false)
  const [url, setUrl] = useState('')
  const [label, setLabel] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  // Nothing to show and nothing to do: render nothing at all rather than an
  // empty heading on every card.
  if (links.length === 0 && !canEdit) return null

  const save = async () => {
    setBusy(true)
    setProblem(null)
    try {
      await addLink(token, { taskId, issueId }, url, label.trim() === '' ? null : label)
      setUrl('')
      setLabel('')
      setAdding(false)
      onChanged()
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const drop = async (link: LinkRow) => {
    setBusy(true)
    setProblem(null)
    try {
      await removeLink(token, link.id)
      onChanged()
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="links">
      {links.length > 0 && (
        <ul className="links__list">
          {links.map((l) => (
            <li key={l.id} className="links__row">
              <a
                className="links__a"
                href={safeHref(l.url)}
                target="_blank"
                // noopener stops the opened page reaching back through
                // window.opener; noreferrer keeps the app's URL out of its logs.
                rel="noopener noreferrer"
              >
                <span className="links__title">{linkTitle(l)}</span>
                {/* Always show where it actually goes — anyone can add a link
                    to their own work and the whole shop can see the list. */}
                <span className="links__host">{hostOf(l.url)}</span>
              </a>
              {canEdit && (
                <button
                  type="button"
                  className="links__x"
                  aria-label={`Remove link ${linkTitle(l)}`}
                  disabled={busy}
                  onClick={() => void drop(l)}
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {problem && <p className="banner banner--tight">{problem}</p>}

      {canEdit &&
        (adding ? (
          <div className="links__form">
            <input
              className="field__input"
              autoFocus
              value={url}
              placeholder="Paste a link"
              inputMode="url"
              onChange={(e) => setUrl(e.target.value)}
            />
            <input
              className="field__input"
              value={label}
              placeholder="What is it? (optional)"
              onChange={(e) => setLabel(e.target.value)}
            />
            <div className="links__actions">
              <button
                type="button"
                className="btn btn--quiet"
                onClick={() => {
                  setAdding(false)
                  setProblem(null)
                  setUrl('')
                  setLabel('')
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn"
                disabled={busy || url.trim() === ''}
                onClick={() => void save()}
              >
                {busy ? 'Adding…' : 'Add link'}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="links__add"
            onClick={() => {
              setAdding(true)
              setProblem(null)
            }}
          >
            + Add a link
          </button>
        ))}
    </div>
  )
}
